import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { accountingAPI } from '../services/api';
import { Spinner } from '../components/Spinner';
import { Plus, Search, Edit, Trash2, Printer, X, QrCode, Download, Truck, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { formatCurrency } from '../utils/formatters';

const headerCellClass = 'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500';
const bodyCellClass = 'px-6 py-4 whitespace-nowrap text-sm text-slate-500';

const INVOICE_LINE_UNITS = ['Adet', 'Kg', 'Metre', 'Metre Kare', 'Paket'];
const IRSALIYE_LINE_UNITS = ['Adet', 'Kg', 'Metre', 'Metre Kare', 'Paket', 'Koli'];

const AccountingLayout = () => (
  <Routes>
    <Route path="/" element={<AccountingOverview />} />
    <Route path="/invoices" element={<Invoices />} />
    <Route path="/irsaliye" element={<Irsaliyeler />} />
    <Route path="/expenses" element={<Expenses />} />
    <Route path="/reports" element={<Reports />} />
    <Route path="/customers" element={<CustomerBalances />} />
  </Routes>
);

const AccountingOverview = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await accountingAPI.getCustomerBalances();
        const balances = response.data;
        const totalReceivables = balances.reduce((sum, customer) => sum + customer.balance, 0);
        const activeCustomers = balances.filter((customer) => customer.total_orders > 0).length;

        setStats({
          totalReceivables,
          activeCustomers,
          totalCustomers: balances.length,
          avgBalance: totalReceivables / balances.length || 0
        });
      } catch (error) {
        console.error('Muhasebe istatistikleri alınamadı:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <Spinner />;
  }

  const cards = [
    { title: 'Toplam Alacaklar', value: formatCurrency(stats.totalReceivables) },
    { title: 'Aktif Müşteriler', value: stats.activeCustomers || 0 },
    { title: 'Toplam Müşteri', value: stats.totalCustomers || 0 },
    { title: 'Ortalama Bakiye', value: formatCurrency(stats.avgBalance) }
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Muhasebe</h1>
            <p className="mt-1 text-sm text-slate-500">Fatura, irsaliye, gider ve bakiye akışını tek düzen içinde takip edin.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => navigate('/accounting/invoices')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Faturalar</button>
            <button onClick={() => navigate('/accounting/irsaliye')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">İrsaliyeler</button>
            <button onClick={() => navigate('/accounting/expenses')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Giderler</button>
            <button onClick={() => navigate('/accounting/customers')} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Bakiyeler</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <div key={card.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-medium text-slate-500">{card.title}</p>
            <p className="mt-2 text-2xl font-bold text-slate-900">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Hızlı İşlemler</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <button onClick={() => navigate('/accounting/invoices')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50">
              <p className="text-sm font-semibold text-slate-900">Faturalar</p>
              <p className="mt-1 text-xs text-slate-500">Tahsilat takibi</p>
            </button>
            <button onClick={() => navigate('/accounting/irsaliye')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50">
              <p className="text-sm font-semibold text-slate-900">İrsaliyeler</p>
              <p className="mt-1 text-xs text-slate-500">Sevk ve teslim belgeleri</p>
            </button>
            <button onClick={() => navigate('/accounting/expenses')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50">
              <p className="text-sm font-semibold text-slate-900">Giderler</p>
              <p className="mt-1 text-xs text-slate-500">Masraf kayıtları</p>
            </button>
            <button onClick={() => navigate('/accounting/reports')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50">
              <p className="text-sm font-semibold text-slate-900">Raporlar</p>
              <p className="mt-1 text-xs text-slate-500">Aylık özet</p>
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Kısa Özet</h2>
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-sm text-slate-500">Cari yoğunluğu</span>
              <span className="text-sm font-semibold text-slate-900">{stats.totalCustomers || 0} müşteri</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-sm text-slate-500">Tahsilat riski</span>
              <span className="text-sm font-semibold text-slate-900">{formatCurrency(stats.totalReceivables)}</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-sm text-slate-500">Aktif hareket</span>
              <span className="text-sm font-semibold text-slate-900">{stats.activeCustomers || 0} aktif cari</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatInvoiceDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
};

const formatInvoiceTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleTimeString('tr-TR');
};

const formatTaxRatePercent = (rate) => {
  const value = toNumber(rate, 0);
  const percent = value <= 1 ? value * 100 : value;
  return Math.round(percent);
};

// Sayıyı yazıya çevirme fonksiyonu (Basit versiyon)
const numberToWords = (n) => {
  const units = ['', 'Bir', 'İki', 'Üç', 'Dört', 'Beş', 'Altı', 'Yedi', 'Sekiz', 'Dokuz'];
  const tens = ['', 'On', 'Yirmi', 'Otuz', 'Kırk', 'Elli', 'Altmış', 'Yetmiş', 'Seksen', 'Doksan'];
  const thousands = ['', 'Bin', 'Milyon', 'Milyar'];

  const convertThreeDigits = (num) => {
    let str = '';
    if (num >= 100) {
      str += (num >= 200 ? units[Math.floor(num / 100)] : '') + 'Yüz';
      num %= 100;
    }
    if (num >= 10) {
      str += tens[Math.floor(num / 10)];
      num %= 10;
    }
    str += units[num];
    return str;
  };

  const amount = toNumber(n, 0);
  if (amount === 0) return 'Sıfır';

  let [integerPart, decimalPart] = amount.toFixed(2).split('.');
  let integer = parseInt(integerPart);
  let words = '';
  let i = 0;

  while (integer > 0) {
    let chunk = integer % 1000;
    if (chunk > 0) {
      let chunkWords = convertThreeDigits(chunk);
      if (i === 1 && chunk === 1) chunkWords = ''; // "Bir Bin" değil "Bin"
      words = chunkWords + thousands[i] + words;
    }
    integer = Math.floor(integer / 1000);
    i++;
  }

  let result = words + ' Türk Lirası';
  if (parseInt(decimalPart) > 0) {
    result += ' ' + convertThreeDigits(parseInt(decimalPart)) + ' Kuruş';
  }

  return result;
};

const InvoicePrintTemplate = ({ invoice, onClose }) => {
  const printRef = useRef();
  const [isExporting, setIsExporting] = useState(false);

  const handlePrint = () => {
    document.body.classList.add('printing-invoice');
    window.print();
    window.setTimeout(() => {
      document.body.classList.remove('printing-invoice');
    }, 500);
  };

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    const toastId = toast.loading('PDF hazırlanıyor...');
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: 794, // A4 width in pixels at 96 DPI
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`${invoice.invoice_number}.pdf`);
      toast.success('PDF indirildi', { id: toastId });
    } catch (error) {
      console.error('PDF Export Error:', error);
      toast.error('PDF oluşturulurken bir hata oluştu', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  if (!invoice) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75 p-4 overflow-y-auto">
      <div className="relative w-full max-w-4xl bg-white shadow-2xl rounded-lg flex flex-col max-h-[95vh]">
        <div className="no-print flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold">Fatura Önizleme</h2>
          <div className="flex gap-2">
            <button 
              onClick={handleDownloadPDF} 
              disabled={isExporting}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
            >
              <Download size={18} /> {isExporting ? 'Hazırlanıyor...' : 'PDF İndir'}
            </button>
            <button onClick={handlePrint} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              <Printer size={18} /> Yazdır
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded text-gray-500">
              <X size={24} />
            </button>
          </div>
        </div>

        <div ref={printRef} id="invoice-print-area" className="p-8 bg-white text-black font-sans text-[12px] overflow-y-auto print:p-0">
          <style type="text/css" dangerouslySetInnerHTML={{ __html: `
            @media print {
              @page { size: A4; margin: 10mm; }
              body.printing-invoice * { visibility: hidden !important; }
              body.printing-invoice #invoice-print-area,
              body.printing-invoice #invoice-print-area * { visibility: visible !important; }
              body.printing-invoice #invoice-print-area {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
              }
              .no-print { display: none !important; }
              body { -webkit-print-color-adjust: exact; }
            }
            .invoice-table th, .invoice-table td { border: 1px solid #ccc; padding: 4px; text-align: left; }
            .invoice-table th { background-color: #f3f4f6; font-weight: bold; }
          `}} />
          
          {/* Header */}
          <div className="flex justify-between mb-6">
            <div className="w-1/2">
              <h1 className="text-sm font-bold uppercase mb-1">ROYSİS ETİKET MATBAACILIK</h1>
              <p className="text-[10px] leading-tight">
                SANAYİ VE TİCARET LİMİTED ŞİRKETİ<br />
                MERKEZ MAH. ATATÜRK CAD. NO:123<br />
                BEYLİKDÜZÜ / İSTANBUL<br />
                Vergi Dairesi: BEYLİKDÜZÜ<br />
                VKN: 1234567890
              </p>
            </div>
            <div className="w-1/3 flex flex-col items-center">
              <div className="mb-2"><QrCode size={60} /></div>
              <p className="text-sm font-bold text-center">e-Arşiv Fatura</p>
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <div className="w-1/2 border p-3 rounded">
              <p className="font-bold text-[10px] mb-1">SAYIN</p>
              <p className="font-bold mb-1 uppercase">{invoice.customer_company || invoice.customer_name}</p>
              <p className="text-[10px] whitespace-pre-wrap">{invoice.customer_address || 'Adres bilgisi yok'}</p>
              <p className="text-[10px] mt-2">VKN/TCKN: {invoice.customer_tax_number || '-'}</p>
            </div>
            <div className="w-1/3">
              <table className="w-full border-collapse text-[10px]">
                <tbody>
                  <tr><td className="border p-1 font-bold">Özelleştirme No:</td><td className="border p-1">{invoice.scenario === 'EARSIVFATURA' ? 'TR1.2' : 'TR1.2'}</td></tr>
                  <tr><td className="border p-1 font-bold">Senaryo:</td><td className="border p-1">{invoice.scenario}</td></tr>
                  <tr><td className="border p-1 font-bold">Fatura Tipi:</td><td className="border p-1">{invoice.invoice_type}</td></tr>
                  <tr><td className="border p-1 font-bold">Fatura No:</td><td className="border p-1">{invoice.invoice_number}</td></tr>
                  <tr><td className="border p-1 font-bold">Fatura Tarihi:</td><td className="border p-1">{formatInvoiceDate(invoice.issue_date)}</td></tr>
                  <tr><td className="border p-1 font-bold">Düzenleme Tarihi:</td><td className="border p-1">{formatInvoiceDate(invoice.created_at)}</td></tr>
                  <tr><td className="border p-1 font-bold">Düzenleme Saati:</td><td className="border p-1">{formatInvoiceTime(invoice.created_at)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-2 text-[10px]">
            <p><span className="font-bold">ETTN:</span> {invoice.ettn}</p>
          </div>

          {/* Items Table */}
          <table className="w-full border-collapse invoice-table mb-4 text-[10px]">
            <thead>
              <tr>
                <th>Sıra No</th>
                <th>Mal Hizmet</th>
                <th>Miktar</th>
                <th>Birim Fiyat</th>
                <th>İskonto (%)</th>
                <th>İskonto Tutarı</th>
                <th>KDV (%)</th>
                <th>KDV Tutarı</th>
                <th>Mal Hizmet Tutarı</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.items || []).map((item, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{item.description}</td>
                  <td>{item.quantity} {item.unit}</td>
                  <td>{formatCurrency(item.unit_price)}</td>
                  <td>%{toNumber(item.discount_rate, 0)}</td>
                  <td>{formatCurrency(item.discount_amount)}</td>
                  <td>%{formatTaxRatePercent(item.tax_rate)}</td>
                  <td>{formatCurrency(item.tax_amount)}</td>
                  <td>{formatCurrency(item.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-6">
            <div className="w-1/3">
              <table className="w-full border-collapse text-[10px]">
                <tbody>
                  <tr>
                    <td className="border p-1 font-bold">Mal Hizmet Toplam Tutarı</td>
                    <td className="border p-1 text-right">{formatCurrency((invoice.items || []).reduce((sum, i) => sum + (toNumber(i.quantity) * toNumber(i.unit_price)), 0))}</td>
                  </tr>
                  <tr>
                    <td className="border p-1 font-bold">Toplam İskonto</td>
                    <td className="border p-1 text-right">{formatCurrency(invoice.discount_amount)}</td>
                  </tr>
                  <tr>
                    <td className="border p-1 font-bold">Hesaplanan KDV</td>
                    <td className="border p-1 text-right">{formatCurrency(invoice.tax_amount)}</td>
                  </tr>
                  <tr className="bg-gray-100">
                    <td className="border p-1 font-bold">Vergiler Dahil Toplam</td>
                    <td className="border p-1 text-right font-bold">{formatCurrency(invoice.total_amount)}</td>
                  </tr>
                  <tr className="bg-gray-200">
                    <td className="border p-1 font-bold">Ödenecek Tutar</td>
                    <td className="border p-1 text-right font-bold text-[12px]">{formatCurrency(invoice.total_amount)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="border p-3 rounded mb-4 text-[10px]">
            <p className="mb-2">Yalnız #{numberToWords(invoice.total_amount)}#</p>
            <p className="font-bold mb-1">NOTLAR:</p>
            <p className="whitespace-pre-wrap">{invoice.notes || 'Not bulunmamaktadır.'}</p>
          </div>

          <div className="text-center text-[9px] mt-8">
            <p>e-Arşiv izni kapsamında elektronik ortamda iletilmiştir. İRSALİYE YERİNE GEÇER.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Invoices = () => {
  const [invoiceData, setInvoiceData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedInvoice, setSelectedOrder] = useState(null); // Detay için
  const [showPrintModal, setShowPrintModal] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    items: [{ description: '', quantity: 1, unit: 'Adet', unit_price: '', discount_rate: 0, tax_rate: 0.20 }],
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    notes: '',
    scenario: 'EARSIVFATURA',
    invoice_type: 'SATIS'
  });

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;

    formData.items.forEach(item => {
      const lineSubtotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      const lineDiscount = lineSubtotal * (parseFloat(item.discount_rate) || 0) / 100;
      const lineTaxable = lineSubtotal - lineDiscount;
      const lineTax = lineTaxable * (parseFloat(item.tax_rate) || 0);
      
      subtotal += lineSubtotal;
      totalDiscount += lineDiscount;
      totalTax += lineTax;
    });

    return {
      subtotal,
      totalDiscount,
      totalTax,
      grandTotal: subtotal - totalDiscount + totalTax
    };
  }, [formData.items]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoiceRes, customerRes] = await Promise.all([
        accountingAPI.getInvoices({ limit: 100 }),
        accountingAPI.getCustomerBalances()
      ]);
      setInvoiceData(invoiceRes.data.invoices || []);
      setCustomers(customerRes.data || []);
    } catch (error) {
      toast.error('Veriler alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit: 'Adet', unit_price: '', discount_rate: 0, tax_rate: 0.20 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems.length ? newItems : [{ description: '', quantity: 1, unit: 'Adet', unit_price: '', discount_rate: 0, tax_rate: 0.20 }] });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const handleViewInvoice = async (id) => {
    try {
      const response = await accountingAPI.getInvoice(id);
      const invoice = response.data || {};
      setSelectedOrder({
        ...invoice,
        items: Array.isArray(invoice.items) ? invoice.items : [],
        total_amount: toNumber(invoice.total_amount, 0),
        discount_amount: toNumber(invoice.discount_amount, 0),
        tax_amount: toNumber(invoice.tax_amount, 0),
      });
      setShowPrintModal(true);
    } catch (error) {
      toast.error('Fatura detayları alınamadı');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await accountingAPI.createInvoice(formData);
      toast.success('Fatura başarıyla oluşturuldu');
      setShowModal(false);
      setFormData({
        customer_id: '',
        items: [{ description: '', quantity: 1, unit: 'Adet', unit_price: '', discount_rate: 0, tax_rate: 0.20 }],
        issue_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        notes: '',
        scenario: 'EARSIVFATURA',
        invoice_type: 'SATIS'
      });
      fetchData();
    } catch (error) {
      toast.error('Fatura oluşturulamadı: ' + (error.response?.data?.error || error.message));
    }
  };

  const filteredInvoices = useMemo(
    () =>
      invoiceData.filter((invoice) =>
        `${invoice.invoice_number} ${invoice.customer_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [invoiceData, searchTerm]
  );

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Faturalar</h1>
          <p className="mt-1 text-sm text-slate-500">Fatura akışını e-Arşiv standartlarında takip edin.</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          data-help-target="accounting-invoice-create"
          className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          Fatura Oluştur
        </button>
      </div>

      {/* Fatura Oluşturma Modalı */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-slate-900">Yeni e-Arşiv Fatura Oluştur</h2>
              <button onClick={() => setShowModal(false)}><X size={24} className="text-slate-400" /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl">
                <div>
                  <label className="block text-sm font-bold text-slate-700">Müşteri Seçin *</label>
                  <select
                    required
                    className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:outline-none"
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                  >
                    <option value="">Seçiniz...</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700">Fatura Tarihi *</label>
                  <input type="date" required className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500" value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700">Vade Tarihi *</label>
                  <input type="date" required className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-bold text-slate-700 uppercase">Fatura Kalemleri</h3>
                  <button type="button" onClick={addItem} className="text-blue-600 text-xs font-bold hover:underline flex items-center gap-1">
                    <Plus size={14} /> KALEM EKLE
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 text-[10px] font-bold text-slate-500 uppercase px-2">
                    <div className="col-span-4">Mal/Hizmet</div>
                    <div className="col-span-1 text-center">Miktar</div>
                    <div className="col-span-1 text-center">Birim</div>
                    <div className="col-span-2 text-right">Birim Fiyat</div>
                    <div className="col-span-1 text-center">İskonto %</div>
                    <div className="col-span-1 text-center">KDV %</div>
                    <div className="col-span-2 text-right">Toplam</div>
                  </div>
                  {formData.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-white border rounded-lg p-2 shadow-sm relative group">
                      <div className="col-span-4">
                        <input placeholder="Açıklama..." className="w-full text-sm border-none focus:ring-0 p-0" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} required />
                      </div>
                      <div className="col-span-1">
                        <input type="number" className="w-full text-sm border-none focus:ring-0 p-0 text-center" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} required />
                      </div>
                      <div className="col-span-1">
                        <select className="w-full text-xs border-none focus:ring-0 p-0" value={item.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)}>
                          {INVOICE_LINE_UNITS.map((unit) => (
                            <option key={unit} value={unit}>{unit}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <input type="number" step="0.01" className="w-full text-sm border-none focus:ring-0 p-0 text-right font-semibold" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} required />
                      </div>
                      <div className="col-span-1">
                        <input type="number" className="w-full text-sm border-none focus:ring-0 p-0 text-center" value={item.discount_rate} onChange={(e) => updateItem(idx, 'discount_rate', e.target.value)} />
                      </div>
                      <div className="col-span-1">
                        <select className="w-full text-xs border-none focus:ring-0 p-0" value={item.tax_rate} onChange={(e) => updateItem(idx, 'tax_rate', e.target.value)}>
                          <option value="0">0</option>
                          <option value="0.01">1</option>
                          <option value="0.10">10</option>
                          <option value="0.20">20</option>
                        </select>
                      </div>
                      <div className="col-span-2 text-right font-bold text-blue-600 text-sm">
                        {formatCurrency((item.quantity * item.unit_price) * (1 - (item.discount_rate/100)) * (1 + parseFloat(item.tax_rate)))}
                      </div>
                      <button type="button" onClick={() => removeItem(idx)} className="absolute -right-2 -top-2 bg-red-100 text-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">Fatura Notu</label>
                  <textarea className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 outline-none" rows="4" placeholder="Fatura üzerinde görünecek notlar..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                </div>
                <div className="bg-slate-50 p-4 rounded-xl space-y-2">
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Mal Hizmet Toplam</span>
                    <span className="font-semibold">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Toplam İskonto</span>
                    <span className="font-semibold">-{formatCurrency(totals.totalDiscount)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Hesaplanan KDV</span>
                    <span className="font-semibold">{formatCurrency(totals.totalTax)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t">
                    <span>Ödenecek Tutar</span>
                    <span className="text-blue-600">{formatCurrency(totals.grandTotal)}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="rounded-lg border border-slate-300 px-6 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgeç</button>
                <button type="submit" className="rounded-lg bg-blue-600 px-8 py-2.5 text-sm font-bold text-white hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all">FATURA OLUŞTUR VE KAYDET</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPrintModal && selectedInvoice && (
        <InvoicePrintTemplate 
          invoice={selectedInvoice} 
          onClose={() => { setShowPrintModal(false); setSelectedOrder(null); }} 
        />
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 p-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Fatura ara..." className="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          <span className="text-sm text-slate-500">{filteredInvoices.length} kayıt</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className={headerCellClass}>Fatura No</th>
                <th className={headerCellClass}>Müşteri</th>
                <th className={headerCellClass}>Toplam Tutar</th>
                <th className={headerCellClass}>İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{invoice.invoice_number}</td>
                  <td className={bodyCellClass}>{invoice.customer_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">{formatCurrency(invoice.total_amount)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3">
                      <button onClick={() => handleViewInvoice(invoice.id)} className="text-blue-600 hover:text-blue-900 flex items-center gap-1">
                        <Printer size={16} /> Görüntüle
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const irsaliyeStatusLabels = {
  draft: 'Taslak',
  shipped: 'Sevk Edildi',
  delivered: 'Teslim Edildi',
  cancelled: 'İptal'
};

const irsaliyeStatusClasses = {
  draft: 'bg-slate-100 text-slate-700',
  shipped: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700'
};

const IrsaliyePrintTemplate = ({ irsaliye, onClose }) => {
  const printRef = useRef();
  const [isExporting, setIsExporting] = useState(false);

  const handlePrint = () => {
    document.body.classList.add('printing-irsaliye');
    window.print();
    window.setTimeout(() => {
      document.body.classList.remove('printing-irsaliye');
    }, 500);
  };

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    const toastId = toast.loading('PDF hazırlanıyor...');
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: 794,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`${irsaliye.irsaliye_number}.pdf`);
      toast.success('PDF indirildi', { id: toastId });
    } catch (error) {
      console.error('PDF Export Error:', error);
      toast.error('PDF oluşturulurken bir hata oluştu', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  if (!irsaliye) return null;

  const deliveryAddress = irsaliye.delivery_address || irsaliye.customer_address || 'Adres bilgisi yok';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75 p-4 overflow-y-auto">
      <div className="relative w-full max-w-4xl bg-white shadow-2xl rounded-lg flex flex-col max-h-[95vh]">
        <div className="no-print flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold">İrsaliye Önizleme</h2>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={isExporting}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
            >
              <Download size={18} /> {isExporting ? 'Hazırlanıyor...' : 'PDF İndir'}
            </button>
            <button onClick={handlePrint} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              <Printer size={18} /> Yazdır
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded text-gray-500">
              <X size={24} />
            </button>
          </div>
        </div>

        <div ref={printRef} id="irsaliye-print-area" className="p-8 bg-white text-black font-sans text-[12px] overflow-y-auto print:p-0">
          <style type="text/css" dangerouslySetInnerHTML={{ __html: `
            @media print {
              @page { size: A4; margin: 10mm; }
              body.printing-irsaliye * { visibility: hidden !important; }
              body.printing-irsaliye #irsaliye-print-area,
              body.printing-irsaliye #irsaliye-print-area * { visibility: visible !important; }
              body.printing-irsaliye #irsaliye-print-area {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
              }
              .no-print { display: none !important; }
              body { -webkit-print-color-adjust: exact; }
            }
            .irsaliye-table th, .irsaliye-table td { border: 1px solid #ccc; padding: 4px; text-align: left; }
            .irsaliye-table th { background-color: #f3f4f6; font-weight: bold; }
          `}} />

          <div className="flex justify-between mb-6">
            <div className="w-1/2">
              <h1 className="text-sm font-bold uppercase mb-1">ROYSİS ETİKET MATBAACILIK</h1>
              <p className="text-[10px] leading-tight">
                SANAYİ VE TİCARET LİMİTED ŞİRKETİ<br />
                MERKEZ MAH. ATATÜRK CAD. NO:123<br />
                BEYLİKDÜZÜ / İSTANBUL<br />
                Vergi Dairesi: BEYLİKDÜZÜ<br />
                VKN: 1234567890
              </p>
            </div>
            <div className="w-1/3 flex flex-col items-center justify-center">
              <Truck size={48} className="mb-2" />
              <p className="text-sm font-bold text-center uppercase">Sevk İrsaliyesi</p>
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <div className="w-1/2 border p-3 rounded">
              <p className="font-bold text-[10px] mb-1">ALICI</p>
              <p className="font-bold mb-1 uppercase">{irsaliye.customer_company || irsaliye.customer_name}</p>
              <p className="text-[10px] whitespace-pre-wrap">{deliveryAddress}</p>
              <p className="text-[10px] mt-2">VKN/TCKN: {irsaliye.customer_tax_number || '-'}</p>
            </div>
            <div className="w-1/3">
              <table className="w-full border-collapse text-[10px]">
                <tbody>
                  <tr><td className="border p-1 font-bold">İrsaliye No:</td><td className="border p-1">{irsaliye.irsaliye_number}</td></tr>
                  <tr><td className="border p-1 font-bold">Düzenleme Tarihi:</td><td className="border p-1">{formatInvoiceDate(irsaliye.issue_date)}</td></tr>
                  <tr><td className="border p-1 font-bold">Sevk/Teslim Tarihi:</td><td className="border p-1">{formatInvoiceDate(irsaliye.delivery_date)}</td></tr>
                  <tr><td className="border p-1 font-bold">Durum:</td><td className="border p-1">{irsaliyeStatusLabels[irsaliye.status] || irsaliye.status}</td></tr>
                  <tr><td className="border p-1 font-bold">Plaka:</td><td className="border p-1">{irsaliye.vehicle_plate || '-'}</td></tr>
                  <tr><td className="border p-1 font-bold">Şoför:</td><td className="border p-1">{irsaliye.driver_name || '-'}</td></tr>
                  <tr><td className="border p-1 font-bold">Taşıyıcı:</td><td className="border p-1">{irsaliye.carrier_name || '-'}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <table className="w-full border-collapse irsaliye-table mb-4 text-[10px]">
            <thead>
              <tr>
                <th>Sıra No</th>
                <th>Mal / Hizmet</th>
                <th>Miktar</th>
                <th>Birim</th>
              </tr>
            </thead>
            <tbody>
              {(irsaliye.items || []).map((item, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{item.description}</td>
                  <td>{item.quantity}</td>
                  <td>{item.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border p-3 rounded mb-6 text-[10px]">
            <p className="font-bold mb-1">NOTLAR:</p>
            <p className="whitespace-pre-wrap">{irsaliye.notes || 'Not bulunmamaktadır.'}</p>
          </div>

          <div className="grid grid-cols-2 gap-8 mt-12 text-[10px]">
            <div className="text-center border-t pt-2">
              <p className="font-bold">Teslim Eden</p>
              <p className="mt-8">İmza / Kaşe</p>
            </div>
            <div className="text-center border-t pt-2">
              <p className="font-bold">Teslim Alan</p>
              <p className="mt-8">İmza / Kaşe</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Irsaliyeler = () => {
  const [irsaliyeData, setIrsaliyeData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedIrsaliye, setSelectedIrsaliye] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertSource, setConvertSource] = useState(null);
  const [showInvoicePrintModal, setShowInvoicePrintModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [convertForm, setConvertForm] = useState({
    items: [],
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    notes: ''
  });

  const convertTotals = useMemo(() => {
    let subtotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;

    convertForm.items.forEach((item) => {
      const lineSubtotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      const lineDiscount = lineSubtotal * (parseFloat(item.discount_rate) || 0) / 100;
      const lineTaxable = lineSubtotal - lineDiscount;
      const lineTax = lineTaxable * (parseFloat(item.tax_rate) || 0);

      subtotal += lineSubtotal;
      totalDiscount += lineDiscount;
      totalTax += lineTax;
    });

    return {
      subtotal,
      totalDiscount,
      totalTax,
      grandTotal: subtotal - totalDiscount + totalTax
    };
  }, [convertForm.items]);

  const [formData, setFormData] = useState({
    customer_id: '',
    items: [{ description: '', quantity: 1, unit: 'Adet' }],
    issue_date: new Date().toISOString().split('T')[0],
    delivery_date: new Date().toISOString().split('T')[0],
    delivery_address: '',
    vehicle_plate: '',
    driver_name: '',
    carrier_name: '',
    notes: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter) params.status = statusFilter;

      const [irsaliyeRes, customerRes] = await Promise.all([
        accountingAPI.getIrsaliyeler(params),
        accountingAPI.getCustomerBalances()
      ]);
      setIrsaliyeData(irsaliyeRes.data.irsaliyeler || []);
      setCustomers(customerRes.data || []);
    } catch (error) {
      toast.error('Veriler alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit: 'Adet' }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      items: newItems.length ? newItems : [{ description: '', quantity: 1, unit: 'Adet' }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const handleViewIrsaliye = async (id) => {
    try {
      const response = await accountingAPI.getIrsaliye(id);
      const irsaliye = response.data || {};
      setSelectedIrsaliye({
        ...irsaliye,
        items: Array.isArray(irsaliye.items) ? irsaliye.items : [],
      });
      setShowPrintModal(true);
    } catch (error) {
      toast.error('İrsaliye detayları alınamadı');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await accountingAPI.updateIrsaliye(id, { status });
      toast.success('Durum güncellendi');
      fetchData();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleOpenConvert = async (irsaliye) => {
    if (irsaliye.invoice_id) {
      toast.error('Bu irsaliye zaten faturaya dönüştürülmüş');
      return;
    }

    if (irsaliye.status === 'cancelled') {
      toast.error('İptal edilmiş irsaliye faturaya dönüştürülemez');
      return;
    }

    try {
      const response = await accountingAPI.getIrsaliye(irsaliye.id);
      const detail = response.data || {};
      const items = (detail.items || []).map((item) => ({
        description: item.description,
        quantity: item.quantity,
        unit: item.unit || 'Adet',
        unit_price: '',
        discount_rate: 0,
        tax_rate: 0.20
      }));

      if (!items.length) {
        toast.error('İrsaliyede kalem bulunamadı');
        return;
      }

      setConvertSource(detail);
      setConvertForm({
        items,
        issue_date: detail.issue_date || new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        notes: detail.notes || ''
      });
      setShowConvertModal(true);
    } catch (error) {
      toast.error('İrsaliye detayları alınamadı');
    }
  };

  const updateConvertItem = (index, field, value) => {
    const newItems = [...convertForm.items];
    newItems[index][field] = value;
    setConvertForm({ ...convertForm, items: newItems });
  };

  const handleConvertSubmit = async (e) => {
    e.preventDefault();

    if (!convertSource?.id) {
      return;
    }

    const hasMissingPrice = convertForm.items.some(
      (item) => item.unit_price === '' || item.unit_price === null || item.unit_price === undefined
    );
    if (hasMissingPrice) {
      toast.error('Tüm kalemler için birim fiyat girin');
      return;
    }

    try {
      const response = await accountingAPI.convertIrsaliyeToInvoice(convertSource.id, convertForm);
      toast.success(`Satış faturası oluşturuldu: ${response.data.invoice_number}`);
      setShowConvertModal(false);
      setConvertSource(null);
      fetchData();
    } catch (error) {
      toast.error('Faturaya dönüştürülemedi: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleViewLinkedInvoice = async (invoiceId) => {
    try {
      const response = await accountingAPI.getInvoice(invoiceId);
      const invoice = response.data || {};
      setSelectedInvoice({
        ...invoice,
        items: Array.isArray(invoice.items) ? invoice.items : [],
        total_amount: toNumber(invoice.total_amount, 0),
        discount_amount: toNumber(invoice.discount_amount, 0),
        tax_amount: toNumber(invoice.tax_amount, 0),
      });
      setShowInvoicePrintModal(true);
    } catch (error) {
      toast.error('Fatura detayları alınamadı');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await accountingAPI.createIrsaliye(formData);
      toast.success('İrsaliye başarıyla oluşturuldu');
      setShowModal(false);
      setFormData({
        customer_id: '',
        items: [{ description: '', quantity: 1, unit: 'Adet' }],
        issue_date: new Date().toISOString().split('T')[0],
        delivery_date: new Date().toISOString().split('T')[0],
        delivery_address: '',
        vehicle_plate: '',
        driver_name: '',
        carrier_name: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('İrsaliye oluşturulamadı: ' + (error.response?.data?.error || error.message));
    }
  };

  const filteredIrsaliyeler = useMemo(
    () =>
      irsaliyeData.filter((irsaliye) =>
        `${irsaliye.irsaliye_number} ${irsaliye.customer_name || ''} ${irsaliye.customer_company || ''}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ),
    [irsaliyeData, searchTerm]
  );

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">İrsaliyeler</h1>
          <p className="mt-1 text-sm text-slate-500">Sevk ve teslim belgelerini oluşturun, yazdırın ve takip edin.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-help-target="accounting-irsaliye-create"
          className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          İrsaliye Oluştur
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-slate-900">Yeni Sevk İrsaliyesi</h2>
              <button onClick={() => setShowModal(false)}><X size={24} className="text-slate-400" /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl">
                <div>
                  <label className="block text-sm font-bold text-slate-700">Müşteri Seçin *</label>
                  <select
                    required
                    className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:outline-none"
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                  >
                    <option value="">Seçiniz...</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700">Düzenleme Tarihi *</label>
                  <input type="date" required className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500" value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700">Sevk/Teslim Tarihi</label>
                  <input type="date" className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500" value={formData.delivery_date} onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700">Teslimat Adresi</label>
                  <textarea className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 outline-none" rows="2" placeholder="Boş bırakılırsa müşteri adresi kullanılır" value={formData.delivery_address} onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-sm font-bold text-slate-700">Araç Plakası</label>
                    <input className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500" value={formData.vehicle_plate} onChange={(e) => setFormData({ ...formData, vehicle_plate: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-bold text-slate-700">Şoför</label>
                      <input className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500" value={formData.driver_name} onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700">Taşıyıcı</label>
                      <input className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500" value={formData.carrier_name} onChange={(e) => setFormData({ ...formData, carrier_name: e.target.value })} />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-bold text-slate-700 uppercase">Sevk Kalemleri</h3>
                  <button type="button" onClick={addItem} className="text-blue-600 text-xs font-bold hover:underline flex items-center gap-1">
                    <Plus size={14} /> KALEM EKLE
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 text-[10px] font-bold text-slate-500 uppercase px-2">
                    <div className="col-span-6">Mal/Hizmet</div>
                    <div className="col-span-2 text-center">Miktar</div>
                    <div className="col-span-2 text-center">Birim</div>
                    <div className="col-span-2 text-right">İşlem</div>
                  </div>
                  {formData.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-white border rounded-lg p-2 shadow-sm relative group">
                      <div className="col-span-6">
                        <input placeholder="Açıklama..." className="w-full text-sm border-none focus:ring-0 p-0" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} required />
                      </div>
                      <div className="col-span-2">
                        <input type="number" min="0" step="0.01" className="w-full text-sm border-none focus:ring-0 p-0 text-center" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} required />
                      </div>
                      <div className="col-span-2">
                        <select className="w-full text-xs border-none focus:ring-0 p-0" value={item.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)}>
                          {IRSALIYE_LINE_UNITS.map((unit) => (
                            <option key={unit} value={unit}>{unit}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <button type="button" onClick={() => removeItem(idx)} className="bg-red-100 text-red-600 rounded-full p-1">
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Notlar</label>
                <textarea className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 outline-none" rows="3" placeholder="İrsaliye üzerinde görünecek notlar..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="rounded-lg border border-slate-300 px-6 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgeç</button>
                <button type="submit" className="rounded-lg bg-blue-600 px-8 py-2.5 text-sm font-bold text-white hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all">İRSALİYE OLUŞTUR</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPrintModal && selectedIrsaliye && (
        <IrsaliyePrintTemplate
          irsaliye={selectedIrsaliye}
          onClose={() => { setShowPrintModal(false); setSelectedIrsaliye(null); }}
        />
      )}

      {showInvoicePrintModal && selectedInvoice && (
        <InvoicePrintTemplate
          invoice={selectedInvoice}
          onClose={() => { setShowInvoicePrintModal(false); setSelectedInvoice(null); }}
        />
      )}

      {showConvertModal && convertSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">İrsaliyeden Satış Faturası Oluştur</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {convertSource.irsaliye_number} · {convertSource.customer_company || convertSource.customer_name}
                </p>
              </div>
              <button onClick={() => { setShowConvertModal(false); setConvertSource(null); }}>
                <X size={24} className="text-slate-400" />
              </button>
            </div>

            <form onSubmit={handleConvertSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl">
                <div>
                  <label className="block text-sm font-bold text-slate-700">Fatura Tarihi *</label>
                  <input type="date" required className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500" value={convertForm.issue_date} onChange={(e) => setConvertForm({ ...convertForm, issue_date: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700">Vade Tarihi *</label>
                  <input type="date" required className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500" value={convertForm.due_date} onChange={(e) => setConvertForm({ ...convertForm, due_date: e.target.value })} />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-slate-700 uppercase mb-2">Fatura Kalemleri</h3>
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 text-[10px] font-bold text-slate-500 uppercase px-2">
                    <div className="col-span-4">Mal/Hizmet</div>
                    <div className="col-span-1 text-center">Miktar</div>
                    <div className="col-span-1 text-center">Birim</div>
                    <div className="col-span-2 text-right">Birim Fiyat</div>
                    <div className="col-span-1 text-center">İskonto %</div>
                    <div className="col-span-1 text-center">KDV %</div>
                    <div className="col-span-2 text-right">Toplam</div>
                  </div>
                  {convertForm.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-white border rounded-lg p-2 shadow-sm">
                      <div className="col-span-4 text-sm text-slate-800">{item.description}</div>
                      <div className="col-span-1 text-sm text-center text-slate-600">{item.quantity}</div>
                      <div className="col-span-1 text-xs text-center text-slate-600">{item.unit}</div>
                      <div className="col-span-2">
                        <input type="number" step="0.01" min="0" required className="w-full text-sm border border-slate-200 rounded px-2 py-1 text-right" value={item.unit_price} onChange={(e) => updateConvertItem(idx, 'unit_price', e.target.value)} />
                      </div>
                      <div className="col-span-1">
                        <input type="number" className="w-full text-sm border border-slate-200 rounded px-2 py-1 text-center" value={item.discount_rate} onChange={(e) => updateConvertItem(idx, 'discount_rate', e.target.value)} />
                      </div>
                      <div className="col-span-1">
                        <select className="w-full text-xs border border-slate-200 rounded px-1 py-1" value={item.tax_rate} onChange={(e) => updateConvertItem(idx, 'tax_rate', e.target.value)}>
                          <option value="0">0</option>
                          <option value="0.01">1</option>
                          <option value="0.10">10</option>
                          <option value="0.20">20</option>
                        </select>
                      </div>
                      <div className="col-span-2 text-right font-bold text-blue-600 text-sm">
                        {formatCurrency((item.quantity * item.unit_price) * (1 - (item.discount_rate / 100)) * (1 + parseFloat(item.tax_rate)))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Fatura Notu</label>
                <textarea className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 outline-none" rows="3" placeholder="Fatura üzerinde görünecek notlar..." value={convertForm.notes} onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })} />
              </div>

              <div className="bg-slate-50 p-4 rounded-xl space-y-2">
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Mal Hizmet Toplam</span>
                  <span className="font-semibold">{formatCurrency(convertTotals.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-red-600">
                  <span>Toplam İskonto</span>
                  <span className="font-semibold">-{formatCurrency(convertTotals.totalDiscount)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Hesaplanan KDV</span>
                  <span className="font-semibold">{formatCurrency(convertTotals.totalTax)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t">
                  <span>Ödenecek Tutar</span>
                  <span className="text-blue-600">{formatCurrency(convertTotals.grandTotal)}</span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowConvertModal(false); setConvertSource(null); }} className="rounded-lg border border-slate-300 px-6 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgeç</button>
                <button type="submit" className="rounded-lg bg-blue-600 px-8 py-2.5 text-sm font-bold text-white hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all">SATIŞ FATURASI OLUŞTUR</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="İrsaliye ara..." className="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Tüm Durumlar</option>
              <option value="draft">Taslak</option>
              <option value="shipped">Sevk Edildi</option>
              <option value="delivered">Teslim Edildi</option>
              <option value="cancelled">İptal</option>
            </select>
            <span className="text-sm text-slate-500">{filteredIrsaliyeler.length} kayıt</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className={headerCellClass}>İrsaliye No</th>
                <th className={headerCellClass}>Müşteri</th>
                <th className={headerCellClass}>Tarih</th>
                <th className={headerCellClass}>Durum</th>
                <th className={headerCellClass}>Fatura</th>
                <th className={headerCellClass}>İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {filteredIrsaliyeler.map((irsaliye) => (
                <tr key={irsaliye.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{irsaliye.irsaliye_number}</td>
                  <td className={bodyCellClass}>{irsaliye.customer_company || irsaliye.customer_name}</td>
                  <td className={bodyCellClass}>{formatInvoiceDate(irsaliye.issue_date)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={irsaliye.status || 'draft'}
                      onChange={(e) => handleStatusChange(irsaliye.id, e.target.value)}
                      className={`rounded-full px-2 py-1 text-xs font-semibold border-0 focus:ring-2 focus:ring-blue-200 ${irsaliyeStatusClasses[irsaliye.status] || irsaliyeStatusClasses.draft}`}
                    >
                      <option value="draft">Taslak</option>
                      <option value="shipped">Sevk Edildi</option>
                      <option value="delivered">Teslim Edildi</option>
                      <option value="cancelled">İptal</option>
                    </select>
                  </td>
                  <td className={bodyCellClass}>
                    {irsaliye.invoice_id ? (
                      <button
                        type="button"
                        onClick={() => handleViewLinkedInvoice(irsaliye.invoice_id)}
                        className="text-green-700 hover:text-green-900 font-medium"
                      >
                        {irsaliye.linked_invoice_number || `#${irsaliye.invoice_id}`}
                      </button>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-wrap items-center gap-3">
                      <button onClick={() => handleViewIrsaliye(irsaliye.id)} className="text-blue-600 hover:text-blue-900 flex items-center gap-1">
                        <Printer size={16} /> Görüntüle
                      </button>
                      {!irsaliye.invoice_id && irsaliye.status !== 'cancelled' && (
                        <button onClick={() => handleOpenConvert(irsaliye)} className="text-emerald-600 hover:text-emerald-900 flex items-center gap-1">
                          <FileText size={16} /> Faturaya Çevir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'Genel',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await accountingAPI.getExpenses();
      setExpenses(response.data.expenses || []);
    } catch (error) {
      toast.error('Giderler alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await accountingAPI.createExpense(formData);
      toast.success('Gider başarıyla kaydedildi');
      setShowModal(false);
      setFormData({
        description: '',
        amount: '',
        category: 'Genel',
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchExpenses();
    } catch (error) {
      toast.error('Gider kaydedilemedi');
    }
  };

  const filteredExpenses = useMemo(
    () =>
      expenses.filter((expense) =>
        `${expense.description || ''} ${expense.category || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [expenses, searchTerm]
  );

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Giderler</h1>
          <p className="mt-1 text-sm text-slate-500">Kategori bazlı masraf kayıtlarını sade tabloda gösterir.</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          Gider Ekle
        </button>
      </div>

      {/* Gider Ekleme Modalı */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-slate-900">Yeni Gider Kaydı</h2>
              <button onClick={() => setShowModal(false)}><X size={24} className="text-slate-400" /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700">Açıklama *</label>
                <input
                  type="text"
                  required
                  placeholder="Gider açıklaması..."
                  className="mt-1 w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 outline-none"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700">Tutar *</label>
                  <div className="relative mt-1">
                    <input
                      type="number"
                      required
                      step="0.01"
                      className="w-full rounded-lg border border-slate-300 p-2.5 pr-8 text-sm focus:border-blue-500 outline-none"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">₺</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700">Kategori</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 outline-none"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="Genel">Genel</option>
                    <option value="Kira">Kira</option>
                    <option value="Fatura">Fatura</option>
                    <option value="Maaş">Maaş</option>
                    <option value="Hammadde">Hammadde</option>
                    <option value="Lojistik">Lojistik</option>
                    <option value="Pazarlama">Pazarlama</option>
                    <option value="Diğer">Diğer</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700">Tarih *</label>
                <input
                  type="date"
                  required
                  className="mt-1 w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700">Notlar</label>
                <textarea
                  className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 outline-none"
                  rows="3"
                  placeholder="Ek bilgiler..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Vazgeç
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-blue-600 px-6 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-colors"
                >
                  Gideri Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 p-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Gider ara..." className="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          <span className="text-sm text-slate-500">{filteredExpenses.length} kayıt</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className={headerCellClass}>Açıklama</th>
                <th className={headerCellClass}>Tutar</th>
                <th className={headerCellClass}>Kategori</th>
                <th className={headerCellClass}>Tarih</th>
                <th className={headerCellClass}>İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {filteredExpenses.map((expense) => (
                <tr key={expense.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900">{expense.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">{formatCurrency(expense.amount)}</td>
                  <td className={bodyCellClass}>{expense.category || 'Yok'}</td>
                  <td className={bodyCellClass}>{new Date(expense.date).toLocaleDateString('tr-TR')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button className="text-slate-600 hover:text-slate-900"><Edit className="h-4 w-4" /></button>
                      <button className="text-red-600 hover:text-red-900"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Reports = () => {
  const summary = [
    { label: 'Aylık Gelir', value: '₺15.000' },
    { label: 'Aylık Gider', value: '₺8.500' },
    { label: 'Net Kar', value: '₺6.500' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Finansal Raporlar</h1>
        <p className="mt-1 text-sm text-slate-500">Özet görünüm ve aylık rakamları tek ekranda izleyin.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Gelir ve Gider</h2>
          <div className="mt-4 flex h-64 items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-500">
            Grafik alanı
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Aylık Özet</h2>
          <div className="mt-4 space-y-3">
            {summary.map((item) => (
              <div key={item.label} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <span className="text-sm text-slate-500">{item.label}</span>
                <span className={`text-sm font-semibold ${item.label === 'Net Kar' ? 'text-green-600' : 'text-slate-900'}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomerBalances = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchCustomerBalances = async () => {
      setLoading(true);
      try {
        const response = await accountingAPI.getCustomerBalances();
        setCustomers(response.data);
      } catch (error) {
        toast.error('Müşteri bakiyeleri alınamadı');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerBalances();
  }, []);

  const filteredCustomers = useMemo(
    () =>
      customers.filter((customer) =>
        `${customer.name} ${customer.company || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [customers, searchTerm]
  );

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Müşteri Bakiyeleri</h1>
        <p className="mt-1 text-sm text-slate-500">Cari bazlı bakiye ve sipariş yoğunluğu tablosu.</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 p-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Cari ara..." className="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          <span className="text-sm text-slate-500">{filteredCustomers.length} kayıt</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className={headerCellClass}>Müşteri</th>
                <th className={headerCellClass}>Firma</th>
                <th className={headerCellClass}>Bakiye</th>
                <th className={headerCellClass}>Toplam Sipariş</th>
                <th className={headerCellClass}>Toplam Alış</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {filteredCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{customer.name}</td>
                  <td className={bodyCellClass}>{customer.company || 'Yok'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                    <span className={customer.balance > 0 ? 'text-red-600' : 'text-green-600'}>
                      {formatCurrency(customer.balance)}
                    </span>
                  </td>
                  <td className={bodyCellClass}>{customer.total_orders}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">{formatCurrency(customer.total_purchases)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccountingLayout;
